import { useState, useEffect, useMemo, useCallback, useRef } from 'preact/hooks';
import { useFilter } from '../../contexts/FilterContext';
import { RiskToggleService } from '../../functions/dataFilters/riskToggle';
import {
  filterRisksByUnitScope,
  isUnitScopeToggleAvailable,
  unitScopeKeysFromClientUnits,
} from '../../functions/dataFilters/unitScopeRiskFiltering';
import { dataService } from '../../functions/apis/dataService';
import { geocodeResolveBatch, type GeocodeBatchPlace } from '../../functions/apis/geocodeService';
import {
  loadWorldCityRecords,
  matchRisksToWorldCities,
  matchRisksToWorldCitiesInWorker,
  normalizeGeospatialPlaceKey,
  readGeospatialMatchCache,
  writeGeospatialMatchCache,
  resolveRiskCountryName,
  type RiskLocationMatch,
} from '../../functions/geospatial';
import type { Risk } from '../../types/api';
import { CoverageWorldMap } from './CoverageWorldMap';
import { applyGeospatialFilters } from './GeospatialSidebarFilters';
import type { UnitCoverageState } from '../../hooks/useUnitCoverage';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import '../../css/coverage-map.css';
import '../../css/coverage-world-map.css';

const BATCH_SIZE = 50;

/** Map focus: GeoJSON country name (or null) and risks whose locations match that area. */
export type GeospatialMapAreaSelection = { country: string | null; risks: Risk[] };

function placeKey(location: string | undefined, country: string | undefined): string {
  return normalizeGeospatialPlaceKey(location, country);
}

interface PendingGeocodePlace {
  key: string;
  location: string;
  country: string;
}

interface PendingGeocodeRun {
  runId: number;
  risks: Risk[];
  places: PendingGeocodePlace[];
}

export interface CoverageMapProps {
  showAllRisks: boolean;
  countryFilter: string;
  cityFilter: string;
  sourceType: string;
  originator: string;
  operatingMode: string;
  riskCategory: string;
  impactTarget: string;
  publicationFromDayMs: number;
  publicationToDayMs: number;
  onRawRisksLoaded?: (risks: Risk[]) => void;
  onMeta?: (meta: { toggleAvailable: boolean }) => void;
  /** When the user focuses a country on the map (or clears), pass country name and risks in that area only. */
  onSelectionChange?: (selection: GeospatialMapAreaSelection) => void;
  /** Aggregated unit locations (GET `/coverage/unit_locations/`); map layer uses this in a later phase. */
  unitCoverage?: UnitCoverageState;
}

/** Map only: filters controlled by parent sidebar. */
export const CoverageMap = ({
  showAllRisks,
  countryFilter,
  cityFilter,
  sourceType,
  originator,
  operatingMode,
  riskCategory,
  impactTarget,
  publicationFromDayMs,
  publicationToDayMs,
  onRawRisksLoaded,
  onMeta,
  onSelectionChange,
  unitCoverage,
}: CoverageMapProps) => {
  const filter = useFilter();
  const scopeUserId = filter.selectedClient?.user_id_no ?? null;

  const [rawRisks, setRawRisks] = useState<Risk[]>([]);
  const [matches, setMatches] = useState<RiskLocationMatch[]>([]);
  const [loadingRisks, setLoadingRisks] = useState(true);
  const [loadingBaseMatches, setLoadingBaseMatches] = useState(false);
  const [loadingGeocodeEnhancement, setLoadingGeocodeEnhancement] = useState(false);
  const [pendingGeocode, setPendingGeocode] = useState<PendingGeocodeRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const activeGeoRunIdRef = useRef(0);

  const filteredRisks = useMemo(
    () =>
      applyGeospatialFilters(
        rawRisks,
        countryFilter,
        cityFilter,
        sourceType,
        originator,
        operatingMode,
        riskCategory,
        impactTarget,
        publicationFromDayMs,
        publicationToDayMs
      ),
    [
      rawRisks,
      countryFilter,
      cityFilter,
      sourceType,
      originator,
      operatingMode,
      riskCategory,
      impactTarget,
      publicationFromDayMs,
      publicationToDayMs,
    ]
  );
  const debouncedFilteredRisks = useDebouncedValue(filteredRisks, 300);

  useEffect(() => {
    let cancelled = false;
    if (filter.loading) {
      setLoadingRisks(true);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      setLoadingRisks(true);
      setError(null);
      try {
        const toggleAvailable = isUnitScopeToggleAvailable(scopeUserId);
        onMeta?.({ toggleAvailable });

        let list: Risk[];
        if (toggleAvailable) {
          list = await RiskToggleService.fetchRisksForScope(scopeUserId, showAllRisks);
          if (!showAllRisks) {
            const scopeKeys = unitScopeKeysFromClientUnits(filter.clientUnits);
            if (scopeKeys.countryKeys.size > 0) {
              list = filterRisksByUnitScope(list, scopeKeys);
            }
          }
        } else {
          list = await dataService.getRisks({ filter_by_units: false });
        }
        if (cancelled) return;
        setRawRisks(list);
        onRawRisksLoaded?.(list);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load.');
        if (!cancelled) setRawRisks([]);
        onRawRisksLoaded?.([]);
      } finally {
        if (!cancelled) setLoadingRisks(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // onMeta / onRawRisksLoaded omitted: parent may pass inline handlers; stable filter deps drive refetch.
  }, [filter.loading, filter.clientUnits, scopeUserId, showAllRisks]);

  useEffect(() => {
    const runId = activeGeoRunIdRef.current + 1;
    activeGeoRunIdRef.current = runId;
    if (loadingRisks) {
      setLoadingBaseMatches(true);
      setLoadingGeocodeEnhancement(false);
      setPendingGeocode(null);
      return;
    }
    if (debouncedFilteredRisks.length === 0) {
      setMatches([]);
      setLoadingBaseMatches(false);
      setLoadingGeocodeEnhancement(false);
      setPendingGeocode(null);
      return;
    }

    let cancelled = false;
    setLoadingBaseMatches(true);
    setLoadingGeocodeEnhancement(false);
    (async () => {
      try {
        const risks = debouncedFilteredRisks;
        let csvMatches: RiskLocationMatch[] = [];
        try {
          csvMatches = await matchRisksToWorldCitiesInWorker(risks);
        } catch {
          csvMatches = await matchRisksToWorldCities(risks);
        }
        if (cancelled || runId !== activeGeoRunIdRef.current) return;

        /* Ensure the main-thread CSV cache is ready so country resolution (name/ISO)
           works for the geocode fallback even when matching ran in the worker. */
        await loadWorldCityRecords().catch(() => undefined);

        const matchedPlaceKeys = new Set(
          csvMatches.map((m) => placeKey(m.risk.location, m.risk.country))
        );
        const unmapped = risks.filter((risk) => !matchedPlaceKeys.has(placeKey(risk.location, risk.country)));
        const seen = new Set<string>();
        const places: PendingGeocodePlace[] = [];
        for (const risk of unmapped) {
          /* Country-first: send a real country name (not an ISO code) to the geocoder. */
          const country = resolveRiskCountryName(risk);
          const key = placeKey(risk.location, country);
          if (!key.replace('|', '').trim() || seen.has(key)) continue;
          seen.add(key);
          places.push({
            key,
            location: (risk.location || '').split(',')[0].trim(),
            country,
          });
        }
        setMatches(csvMatches);
        setPendingGeocode({ runId, risks: unmapped, places });
        setLoadingGeocodeEnhancement(places.length > 0);
      } catch (err) {
        if (!cancelled && runId === activeGeoRunIdRef.current) {
          setError((p) => p ?? (err instanceof Error ? err.message : 'Geocoding failed'));
          setPendingGeocode(null);
          setLoadingGeocodeEnhancement(false);
        }
      } finally {
        if (!cancelled && runId === activeGeoRunIdRef.current) setLoadingBaseMatches(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadingRisks, debouncedFilteredRisks]);

  useEffect(() => {
    if (!pendingGeocode || pendingGeocode.places.length === 0) return;
    if (pendingGeocode.runId !== activeGeoRunIdRef.current) return;
    let cancelled = false;

    const risksByKey = new Map<string, Risk[]>();
    for (const risk of pendingGeocode.risks) {
      const key = placeKey(risk.location, resolveRiskCountryName(risk));
      if (!key) continue;
      const existing = risksByKey.get(key);
      if (existing) existing.push(risk);
      else risksByKey.set(key, [risk]);
    }

    (async () => {
      try {
        const geoByPlace = new Map<string, GeocodeBatchPlace>();
        const placesForApi: PendingGeocodePlace[] = [];
        for (const place of pendingGeocode.places) {
          const cached = readGeospatialMatchCache(place.key);
          if (cached) {
            geoByPlace.set(place.key, {
              location: place.location,
              country: place.country,
              lat: cached.lat,
              lng: cached.lng,
              source: cached.source,
              city: cached.matchedCity,
              country_resolved: cached.matchedCountry,
            });
          } else {
            placesForApi.push(place);
          }
        }
        const appendedKeys = new Set<string>();
        const appendMatchesForKeys = (keys: string[]) => {
          const newMatches: RiskLocationMatch[] = [];
          for (const key of keys) {
            if (appendedKeys.has(key)) continue;
            const geo = geoByPlace.get(key);
            if (!geo) continue;
            appendedKeys.add(key);
            const relatedRisks = risksByKey.get(key) || [];
            for (const risk of relatedRisks) {
              newMatches.push({
                risk,
                lat: geo.lat,
                lng: geo.lng,
                matchedCity: geo.city,
                matchedCountry: geo.country_resolved,
                matchScore: 0.9,
              });
            }
          }
          if (newMatches.length > 0) {
            setMatches((prev) => {
              if (pendingGeocode.runId !== activeGeoRunIdRef.current) return prev;
              return [...prev, ...newMatches];
            });
          }
        };
        appendMatchesForKeys([...geoByPlace.keys()]);

        if (placesForApi.length === 0) {
          if (!cancelled && pendingGeocode.runId === activeGeoRunIdRef.current) {
            setLoadingGeocodeEnhancement(false);
          }
          return;
        }
        for (let i = 0; i < placesForApi.length && !cancelled; i += BATCH_SIZE) {
          const placeBatch = placesForApi.slice(i, i + BATCH_SIZE);
          try {
            const batch = await geocodeResolveBatch(
              placeBatch.map((p) => ({ location: p.location, country: p.country }))
            );
            if (cancelled || pendingGeocode.runId !== activeGeoRunIdRef.current) return;
            for (const res of batch.results) {
              const key = placeKey(res.location, res.country);
              geoByPlace.set(key, res);
              writeGeospatialMatchCache(key, {
                lat: res.lat,
                lng: res.lng,
                matchedCity: res.city,
                matchedCountry: res.country_resolved,
                source: res.source,
              });
            }
            appendMatchesForKeys(placeBatch.map((p) => p.key));
          } catch {
            /* silent per batch; continue next */
          }
        }
      } catch (err) {
        if (!cancelled && pendingGeocode.runId === activeGeoRunIdRef.current) {
          setError((p) => p ?? (err instanceof Error ? err.message : 'Geocoding failed'));
        }
      } finally {
        if (!cancelled && pendingGeocode.runId === activeGeoRunIdRef.current) {
          setLoadingGeocodeEnhancement(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pendingGeocode]);

  const loading = loadingRisks || loadingBaseMatches;
  const displayError = error || unitCoverage?.error || null;

  const handleSelectionChange = useCallback(
    (country: string | null, risks: Risk[]) => {
      onSelectionChange?.({ country, risks });
    },
    [onSelectionChange]
  );

  return (
    <div className="coverage-map coverage-map--map-only">
      {displayError && (
        <div className="coverage-map__toast" role="alert">
          {displayError}
        </div>
      )}
      <div className="coverage-map__map-section coverage-map__map-section--embedded">
        {loading && <div className="coverage-map__loading-dot" aria-hidden />}
        <CoverageWorldMap
          matches={matches}
          loading={loadingBaseMatches}
          filterCountry={countryFilter}
          filterCity={cityFilter}
          onSelectionChange={onSelectionChange ? handleSelectionChange : undefined}
          unitLocations={unitCoverage?.locations ?? []}
        />
      </div>
    </div>
  );
}
